import { useState, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import { frequentlyBoughtTogetherStorage } from "../../service/frequentlybought.service";
import { useCart } from "../../../contexts/CartContext";
import { 
  FrequentlyBoughtProduct, 
  FrequentlyBoughtTogetherError, 
  FrequentlyBoughtTogetherProps
} from "../../dto/frequentlybought.dto";
import FrequentlyBoughtCards from './FrequentlyBoughtCards';
import './styles/frequentlybought.css';
import LoadingSpinner from '../LodingSpiner/LodingSpiner';

const FrequentlyBought: React.FC<FrequentlyBoughtTogetherProps> = ({ currentProduct }) => {
  const [products, setProducts] = useState<FrequentlyBoughtProduct[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<FrequentlyBoughtTogetherError[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();
  const navigate = useNavigate();
  useEffect(() => {
    console.log('FrequentlyBought useEffect triggered for product:', currentProduct?._id, currentProduct?.title);
    const loadProducts = async () => {
      try {
        setLoading(true);
        
        if (currentProduct) {
          // Always start fresh for each product view
          frequentlyBoughtTogetherStorage.clearProducts();
          
          // Add current product
          frequentlyBoughtTogetherStorage.addProduct(currentProduct, false); // Don't dispatch for current product

          // Fetch paired products for this product only
          try {
            await frequentlyBoughtTogetherStorage.fetchPairedProducts(currentProduct);
          } catch (error) {
            console.error('Error fetching paired products:', error);
          }
        }

        // Get final products after additions
        const finalProducts = frequentlyBoughtTogetherStorage.getProducts();
        console.log('Final products count:', finalProducts.length, finalProducts.map(p => ({ id: p.id, title: p.title })));
        
        setProducts([...finalProducts]);
        setErrors([]);
        const defaultSelectedIds = finalProducts
          .filter((p) => p.defaultSelected)
          .map((p) => p.id);
        setSelectedIds(defaultSelectedIds);
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading products:', error);
        setLoading(false);
      }
    };

    loadProducts();
    
    // Only listen for external storage changes, not internal updates
    const handleExternalChange = () => {
      const updatedProducts = frequentlyBoughtTogetherStorage.getProducts();
      setProducts(updatedProducts);
      const updatedSelectedIds = updatedProducts
        .filter((p) => p.defaultSelected)
        .map((p) => p.id);
      setSelectedIds(updatedSelectedIds);
    };

    window.addEventListener('storage', handleExternalChange);

    return () => {
      window.removeEventListener('storage', handleExternalChange);
    };
  }, [currentProduct?._id]); // Only depend on product ID, not the whole object

  const toggle = (id: string) => {
    const isCurrentlySelected = selectedIds.includes(id);
    
    if (isCurrentlySelected) {
      frequentlyBoughtTogetherStorage.removeProduct(id);
      const newSelectedIds = selectedIds.filter((x) => x !== id);
      setSelectedIds(newSelectedIds);
      const updatedProducts = frequentlyBoughtTogetherStorage.getProducts();
      setProducts(updatedProducts);
      window.dispatchEvent(new CustomEvent('frequentlyBoughtTogetherUpdated'));
    } else {
      const newSelectedIds = [...selectedIds, id];
      setSelectedIds(newSelectedIds);
      frequentlyBoughtTogetherStorage.updateProductSelection(id, true);
    }
  };

  const total = products
    .filter((p) => selectedIds.includes(p.id))
    .reduce((s, p) => s + p.price, 0);

  const selectedCount = selectedIds.length;

  const handleAddAllToCart = async () => {
    if (selectedCount === 0) {
      alert('Please select at least one product');
      return;
    }

    try {
      const selectedProducts = products.filter((p) => selectedIds.includes(p.id));
      
      for (const product of selectedProducts) {
        if (product.productData) {
          let imgSrc = '';
          if (Array.isArray(product.productData.images) && product.productData.images.length > 0 && product.productData.images[0].url) {
            imgSrc = product.productData.images[0].url;
          }
          const cartProduct = {
            id: product.productData._id,
            _id: product.productData._id,
            title: product.productData.title || product.productData.name || 'Unnamed Product',
            name: product.productData.title || product.productData.name || 'Unnamed Product',
            slug: product.productData.slug,
            price: product.productData.price || 0,
            imageUrl: imgSrc,
            description: product.productData.description || ''
          };
          
          await addToCart(cartProduct, 1);
        }
      }
      
      alert(`Added ${selectedCount} item(s) to cart — Total ₹ ${total}`);
    } catch (error) {
      alert('Failed to add products to cart');
    }
  };

  const clearErrors = () => {
    frequentlyBoughtTogetherStorage.clearErrors();
    setErrors([]);
  };

  const handleCardClick = (id: string) => {
    const product = products.find(p => p.id === id);
    if (product && product.productData) {
      navigate(`/product/${product.productData.slug || product.productData._id}`);
    }
  };
  const fbItems = products.map(product => ({
    id: product.id,
    title: product.title,
    price: `₹ ${Math.round(product.price).toLocaleString()}`,
    img: product.image
  }));

  if (loading) {
    return (
      <section className="frequently-bought">
        <div className="fb-header">
          <div className="fb-title">Frequently Bought Together</div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '2rem' }}>
          <LoadingSpinner size="large" />
        </div>
      </section>
    );
  }

  if (products.length === 0 && errors.length === 0) {
    return null; 
  }

  return (
    <section className="frequently-bought" aria-label="Frequently bought together">
      <div className="fb-header">
        <div className="fb-title">Frequently Bought Together</div>
        <div className="fb-action">
          <button 
            className="rs-btn"
            onClick={handleAddAllToCart}
            disabled={selectedCount === 0}
          >
            Add {selectedCount} item{selectedCount !== 1 ? 's' : ''} to Cart
          </button>
        </div>
      </div>

      {errors.length > 0 && (
        <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fef2f2', borderRadius: '0.5rem', border: '1px solid #fecaca' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: '600', color: '#991b1b', margin: 0 }}>Messages:</h3>
            <button onClick={clearErrors} style={{ fontSize: '0.75rem', color: '#dc2626', background: 'none', border: 'none', textDecoration: 'underline', cursor: 'pointer' }}>
              Clear Messages
            </button>
          </div>
          {errors.map((error, index) => (
            <div key={index} style={{ fontSize: '0.875rem', color: '#b91c1c', marginBottom: '0.25rem' }}>
              • {error.message}
            </div>
          ))}
        </div>
      )}

      {/* Show all products */}
      <FrequentlyBoughtCards 
        items={fbItems}
        selectedIds={selectedIds}
        onToggle={toggle}
        onCardClick={handleCardClick}
      />
    </section>
  );
};

export default FrequentlyBought;
